/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { MatSliderChange } from '@angular/material/slider';
import { ScheduleTaskChangeDateAfterCompleteType, ScheduleTaskType, ScheduleType } from '@gh/builder-shared-data';

import {
  AbstractSnapshotFormComponent,
  DateTimeTypes,
  EnumDictionary,
  FormState,
  FormStateBuilder,
  FormStateSnapshot,
  LocalizationBusinessService,
  NumberTypes,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import { FormMode, getControlValue, setControlValue, validationError } from '@gh/core-ui';
import { currentNormalizedDate, isDateInvalid, normalizeDate, NULL } from '@gh/core-util';

import { includes, isNil } from 'lodash';

import { ScheduleTaskInfo } from '../../../resources/schedule-task-details.model';

@Component({
  selector: 'gh-schedule-task-details-top-form',
  template: require('./schedule-task-details-top-form.component.html'),
  styles: [require('./schedule-task-details-top-form.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleTaskDetailsTopFormComponent extends AbstractSnapshotFormComponent<ScheduleTaskInfo>
  implements OnInit {

  @Input() type: ScheduleTaskType;
  @Input() fromCriticalPath: boolean;
  @Input() includedTasksExist: boolean;
  @Input() scheduleTaskTypes: EnumDictionary<ScheduleTaskType>;
  @Input() changeDateAfterCompleteTypes: EnumDictionary<ScheduleTaskChangeDateAfterCompleteType>;
  @Input() scheduleType: ScheduleType;

  minConfirmedForDate: Date;
  minConfirmedOnDate: Date;

  constructor(private formStateBuilder: FormStateBuilder,
              private localizationBusinessService: LocalizationBusinessService,
              private translationService: TranslationService) {
    super();
    this.formattedCompletePctSliderLabel = this.formattedCompletePctSliderLabel.bind(this);
    this.isForDateAllowed = this.isForDateAllowed.bind(this);
    this.isOnDateAllowed = this.isOnDateAllowed.bind(this);
  }

  get completionPctValue(): number {
    return getControlValue(this.form.group, 'completionPct');
  }

  get completionPctStateValue(): Maybe<number> {
    return this.state.value.completionPct;
  }

  get isConstructionType(): boolean {
    return this.scheduleType === ScheduleType.Construction;
  }

  get startDate(): string {
    return getControlValue(this.form.group, 'startDate');
  }

  get endDate(): string {
    return getControlValue(this.form.group, 'endDate');
  }

  get canChangeCompletion(): boolean {
    return !this.includedTasksExist;
  }

  formattedDurationControlValue(): string {
    return this.state.value.duration
      && this.localizationBusinessService.formatHoursDuration(this.state.value.duration)
      || '';
  }

  formattedCompletionPctControlValue(): string {
    return `${this.state.value.completionPct} ${this.translationService.get('lbl_pct')}`;
  }

  formattedCompletePctSliderLabel(value: number): string {
    return `${NumberTypes.Percents.format(value)}${this.translationService.get('lbl_pct')}`;
  }

  onCompletePctSliderInput({ value }: MatSliderChange): void {
    setControlValue(this.form.group, 'completionPct', value);
  }

  isViewMode(): boolean {
    return this.mode === FormMode.ViewMode;
  }

  isForDateAllowed(date: Date): boolean {
    return this.form && normalizeDate(date) >= this.minConfirmedForDate;
  }

  isOnDateAllowed(date: Date): boolean {
    return this.form && normalizeDate(date) >= this.minConfirmedOnDate;
  }

  protected buildForm(snapshot: FormStateSnapshot<ScheduleTaskInfo>): FormState<ScheduleTaskInfo> {
    this.minConfirmedOnDate = snapshot.value.confirmedOnDate || currentNormalizedDate();
    this.minConfirmedForDate = snapshot.value.confirmedForDate || currentNormalizedDate();
    return this.formStateBuilder.buildPlain<ScheduleTaskInfo>({
      snapshot,
      mode: this.mode,
      enabled: whenCreateOrEditMode(),
      children: {
        name: {
          required: true,
          enabled: this.isNameControlEnabled(),
        },
        description: void 0,
        startDate: { type: DateTimeTypes.ShortDate, enabled: false },
        endDate: { type: DateTimeTypes.ShortDate, enabled: false },
        confirmedOnDate: {
          type: DateTimeTypes.ShortDate,
          visible: this.scheduleType === ScheduleType.Construction,
          validators: this.scheduleType === ScheduleType.Construction
            ? [(control: AbstractControl) =>
              isNil(control.value) || isDateInvalid(control.value) || this.isOnDateAllowed(control.value)
                ? NULL
                : { 'date': validationError('msg_field_invalid_format') }]
            : void 0,
        },
        confirmedForDate: {
          type: DateTimeTypes.ShortDate,
          visible: this.scheduleType === ScheduleType.Construction,
          validators: this.scheduleType === ScheduleType.Construction
            ? [(control: AbstractControl) =>
              isNil(control.value) || isDateInvalid(control.value) || this.isForDateAllowed(control.value)
                ? NULL
                : { 'date': validationError('msg_field_invalid_format') }]
            : void 0,
        },
        duration: {
          type: NumberTypes.Days,
          enabled: false,
        },
        resource: { enabled: false },
        completionPct: { enabled: false },
        isExcludeFromGanttChart: void 0,
        orderNumber: {
          enabled: false,
          visible: this.scheduleType === ScheduleType.Construction
            && this.type === ScheduleTaskType.CostCenter,
        },
        costCenterCode: {
          enabled: false,
          visible: this.scheduleType === ScheduleType.Construction
            && this.type === ScheduleTaskType.CostCenter,
        },
        confirmedWith: {
          visible: this.scheduleType === ScheduleType.Construction,
        },
        changeDateAfterComplete: {
          visible: this.scheduleType === ScheduleType.PreConstruction,
        },
        scheduleType: void 0,
      },
    });
  }

  private isNameControlEnabled(): boolean {
    return includes([ScheduleTaskType.Empty, ScheduleTaskType.Summary], this.type)
      || (this.type === ScheduleTaskType.CostCenter && !this.fromCriticalPath);
  }
}
